import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
} from 'react';

import {
	Box,
	Button,
	Callout,
	Divider,
	Group,
	SkeletonCircle,
	SkeletonRect,
	Stack,
	Text,
} from '@ekhodealer/ekho-common/rev';

import type { ConversationMessage } from '@ekhodealer/ekho-common';

import { useGetConversationMessages } from '@common/api/conversations/useGetConversationMessages';
import { formatRelativeDate, isDifferentDay } from '@common/lib/time';

import { MessageBubble } from './MessageBubble';
import { SystemMessage } from './SystemMessage';

import './ConversationView.scss';

interface GroupedMessage {
	message: ConversationMessage;
	isFirstInGroup: boolean;
	isLastInGroup: boolean;
	showDaySeparator: boolean;
	separatorDate?: string;
}

/** Maximum gap between messages in the same sender group (2 minutes). */
const GROUP_WINDOW_MS = 2 * 60_000;

function groupMessagesBySender(messages: ConversationMessage[]): GroupedMessage[] {
	if (messages.length === 0) return [];

	const result: GroupedMessage[] = [];

	for (let i = 0; i < messages.length; i++) {
		const current = messages[i];
		const prev = i > 0 ? messages[i - 1] : null;
		const next = i < messages.length - 1 ? messages[i + 1] : null;

		const showDaySeparator = prev == null || isDifferentDay(prev.createdAt, current.createdAt);

		const isSameGroupAsPrev =
			prev?.senderType === current.senderType &&
			!isDifferentDay(prev.createdAt, current.createdAt) &&
			Math.abs(new Date(current.createdAt).getTime() - new Date(prev.createdAt).getTime()) <=
				GROUP_WINDOW_MS;

		const isSameGroupAsNext =
			next?.senderType === current.senderType &&
			!isDifferentDay(current.createdAt, next.createdAt) &&
			Math.abs(new Date(next.createdAt).getTime() - new Date(current.createdAt).getTime()) <=
				GROUP_WINDOW_MS;

		result.push({
			message: current,
			isFirstInGroup: !isSameGroupAsPrev,
			isLastInGroup: !isSameGroupAsNext,
			showDaySeparator,
			separatorDate: showDaySeparator ? current.createdAt : undefined,
		});
	}

	return result;
}

interface ConversationViewProps {
	leadId: string | undefined;
	conversationId: string | undefined;
}

export interface ConversationViewHandle {
	focusMessageList: () => void;
}

const MessagesEmptyState = (): React.ReactNode => {
	return (
		<Stack itemsSpacing='8' padding={{ y: 32 }} itemsAlignX='center'>
			<Text size='body' weight='semibold' align='center'>
				No messages in this conversation yet
			</Text>
			<Text size='body' color='secondary' align='center'>
				Messages will appear here once the conversation begins.
			</Text>
		</Stack>
	);
};

/**
 * Skeleton rows matching the real MessageBubble layout.
 * AI (right): header right-aligned, body fills full content width.
 * Lead (left): header left-aligned, body wraps to content width.
 */
const SKELETON_ROWS = [
	{ side: 'right' as const, bodyHeight: 56 },
	{ side: 'left' as const, bodyHeight: 56 },
	{ side: 'right' as const, bodyHeight: 56 },
];

const ConversationSkeleton = (): React.ReactNode => {
	return (
		<Stack itemsSpacing='20' padding={{ top: 20, bottom: 8 }} ariaLabel='Loading messages'>
			{/* Day separator skeleton */}
			<Group itemsSpacing='12' itemsAlignY='center' itemsAlignX='center'>
				<SkeletonRect width={64} height={14} rounding='8' />
			</Group>

			{SKELETON_ROWS.map((row, i) => (
				<Stack key={i} itemsSpacing='6' itemsAlignX={row.side === 'left' ? 'start' : 'end'}>
					{/* Avatar + header row */}
					<Group
						itemsSpacing='8'
						itemsAlignY='center'
						itemsAlignX={row.side === 'left' ? 'start' : 'end'}>
						{row.side === 'left' && <SkeletonCircle size='small' />}
						<SkeletonRect width={120} height={12} rounding='8' />
						{row.side === 'right' && <SkeletonCircle size='small' />}
					</Group>

					{/* Message body bubble */}
					<SkeletonRect width={240} height={row.bodyHeight} rounding='12' />
				</Stack>
			))}
		</Stack>
	);
};

const MessagesErrorState = ({ onRetry }: { onRetry: () => void }): React.ReactNode => {
	return (
		<Callout variant='error' title='Failed to load messages' message='Please try again.'>
			<Button variant='ghost' size='small' onPress={onRetry}>
				Try again
			</Button>
		</Callout>
	);
};

export const ConversationView = forwardRef<ConversationViewHandle, ConversationViewProps>(
	({ leadId, conversationId }, ref) => {
		const {
			messages,
			isLoading,
			isError,
			refetch,
			hasNextPage,
			isFetchingNextPage,
			fetchNextPage,
		} = useGetConversationMessages(leadId, conversationId);

		const scrollContainerRef = useRef<HTMLDivElement>(null);
		const sentinelRef = useRef<HTMLDivElement>(null);
		const prevGroupedLengthRef = useRef(0);
		const prevScrollHeightRef = useRef(0);

		useImperativeHandle(ref, () => ({
			focusMessageList: () => {
				scrollContainerRef.current?.focus();
			},
		}));

		// Reverse messages to chronological order (oldest first, newest at bottom)
		const groupedMessages = useMemo(
			() => groupMessagesBySender([...messages].reverse()),
			[messages],
		);

		// Scroll management: scroll to bottom on initial load, preserve position when older messages prepend
		useLayoutEffect(() => {
			const container = scrollContainerRef.current;

			if (groupedMessages.length === 0) {
				prevGroupedLengthRef.current = 0;
				prevScrollHeightRef.current = 0;
				return;
			}

			if (!container) return;

			const wasEmpty = prevGroupedLengthRef.current === 0;

			if (wasEmpty) {
				// Initial load — scroll to bottom to show newest messages
				container.scrollTop = container.scrollHeight;
			} else {
				// Older messages prepended at top — adjust scroll to keep viewport in place
				const heightDiff = container.scrollHeight - prevScrollHeightRef.current;
				if (heightDiff > 0) {
					container.scrollTop += heightDiff;
				}
			}

			prevGroupedLengthRef.current = groupedMessages.length;
			prevScrollHeightRef.current = container.scrollHeight;
		}, [groupedMessages]);

		// IntersectionObserver to trigger loading older messages when scrolling to top
		useEffect(() => {
			const sentinel = sentinelRef.current;
			if (!sentinel) return;

			const observer = new IntersectionObserver(
				(entries) => {
					const entry = entries[0];
					if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
						fetchNextPage();
					}
				},
				{
					root: scrollContainerRef.current,
					rootMargin: '100px 0px 0px 0px',
					threshold: 0,
				},
			);

			observer.observe(sentinel);

			return () => {
				observer.disconnect();
			};
		}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

		return (
			<div data-component='conversation-view'>
				{isLoading ? (
					<ConversationSkeleton />
				) : isError ? (
					<MessagesErrorState onRetry={refetch} />
				) : messages.length === 0 ? (
					<MessagesEmptyState />
				) : (
					<div
						ref={scrollContainerRef}
						className='conversation-view__messages'
						role='log'
						aria-label='Conversation messages'
						aria-live='polite'
						tabIndex={0}>
						{/* Sentinel element for infinite scroll */}
						<div
							ref={sentinelRef}
							className='conversation-view__sentinel'
							aria-hidden='true'
						/>
						{isFetchingNextPage && (
							<Group
								itemsAlignX='center'
								padding={{ y: 8 }}
								className='conversation-view__loading-older'>
								<SkeletonRect width='60%' height='32px' />
							</Group>
						)}
						{groupedMessages.map((grouped) => (
							<Box
								key={grouped.message.id}
								margin={{ top: grouped.isFirstInGroup ? 20 : 4 }}>
								<Stack itemsSpacing='8'>
									{grouped.showDaySeparator && grouped.separatorDate && (
										<Divider
											label={formatRelativeDate(grouped.separatorDate, {
												includeTime: false,
											})}
										/>
									)}
									{grouped.message.senderType === 'system' ? (
										<SystemMessage
											body={
												grouped.message.systemDescription ??
												grouped.message.body
											}
											createdAt={grouped.message.createdAt}
										/>
									) : (
										<MessageBubble
											body={grouped.message.body}
											senderType={grouped.message.senderType}
											senderName={grouped.message.senderName}
											createdAt={grouped.message.createdAt}
											isFirstInGroup={grouped.isFirstInGroup}
											isLastInGroup={grouped.isLastInGroup}
										/>
									)}
								</Stack>
							</Box>
						))}
					</div>
				)}
			</div>
		);
	},
);

ConversationView.displayName = 'ConversationView';
